import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { 
  User, 
  Incident, 
  Category, 
  Unit, 
  Role,
  LoginRequest, 
  ApiLoginResponse,
  ApiResponse, 
  PaginatedResponse,
  IncidentFormData,
  UserFormData,
  DashboardStats,
  LHDForm,
  LHDFormListResponse,
  LHDStatus,
} from '../types/index';
import { API_BASE_URL } from '../config/env';

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshInFlight: Promise<string | null> | null = null;

function clearAuthSession(): void {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
}

async function requestNewAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API_BASE_URL}/auth/refresh`, { refreshToken }, { timeout: 10000 })
      .then((res) => {
        const data = res.data?.data || {};
        const accessToken = data.access_token || data.token;
        if (!accessToken) return null;
        localStorage.setItem('access_token', accessToken);
        if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
        return accessToken as string;
      })
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    const BASE_URL = API_BASE_URL;
    
    this.api = axios.create({
      baseURL: BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-store, no-cache, must-revalidate, proxy-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - Logout ONLY on 401 (token expired/invalid), NOT on 403 (permission denied)
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (!error.response) {
          return Promise.reject(error);
        }
        
        const status = error.response?.status;
        const requestUrl = String(error.config?.url || '');
        const isLoginRequest = requestUrl.includes('/auth/login');
        const isRefreshRequest = requestUrl.includes('/auth/refresh');
        const isPublicAuthRequest = isLoginRequest || requestUrl.includes('/auth/forgot-password') || requestUrl.includes('/users/auth/reset-password');
        
        // Only logout on 401 (Unauthorized) - token expired or invalid
        // 403 (Forbidden) means user is authenticated but doesn't have permission - DO NOT LOGOUT
        if (status === 401) {
          const originalRequest = error.config as RetryableRequestConfig | undefined;
          if (originalRequest && !originalRequest._retry && !isRefreshRequest && !isPublicAuthRequest) {
            originalRequest._retry = true;
            const nextToken = await requestNewAccessToken();
            if (nextToken) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${nextToken}`;
              return this.api(originalRequest);
            }
          }

          if (!isPublicAuthRequest && window.location.pathname !== '/' && window.location.pathname !== '/login') {
            clearAuthSession();
            if (typeof window !== 'undefined') {
              window.location.href = '/';
            }
          }
          
          return Promise.reject(error);
        }
        
        // For 403 (Forbidden) and all other errors (400, 404, 500, etc.) - DO NOT LOGOUT
        // Just reject so the UI can show the error message
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<AxiosResponse<ApiLoginResponse>> {
    try {
      return await this.api.post<ApiLoginResponse>('/auth/login', credentials);
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async logout(): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.post('/auth/logout');
  }

  async refreshToken(refreshToken: string): Promise<AxiosResponse<ApiLoginResponse>> {
    return this.api.post('/auth/refresh', { refreshToken });
  }

  async forgotPassword(identifier: string): Promise<AxiosResponse<ApiResponse<{ resetPath?: string }>>> {
    return this.api.post('/auth/forgot-password', { identifier });
  }

  async confirmPasswordReset(token: string, newPassword: string, confirmPassword: string): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.post('/users/auth/reset-password', {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  }

  async getCurrentUser(): Promise<AxiosResponse<ApiResponse<User>>> {
    return this.api.get('/auth/me');
  }

  /** Current user with unit & department for header. GET /api/users/me */
  async getUsersMe(): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.get('/users/me');
  }

  /** Update current user profile. PUT /api/users/me */
  async updateUserProfile(data: {
    full_name?: string;
    email?: string;
    phone?: string;
    bio?: string;
    new_password?: string;
  }): Promise<AxiosResponse<ApiResponse<User>>> {
    return this.api.put('/users/me', data);
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.put('/auth/change-password', { oldPassword, newPassword });
  }

  // Users endpoints
  async getUsers(
    page = 1,
    limit = 10,
    search?: string,
    filters?: Record<string, any>
  ): Promise<AxiosResponse<PaginatedResponse<User>>> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (search) params.append('search', search);
    if (filters) {
      Object.keys(filters).forEach((k) => {
        const v = filters[k];
        if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
      });
    }
    return this.api.get(`/users?${params}`);
  }

  async getUserById(id: number): Promise<AxiosResponse<ApiResponse<User>>> {
    return this.api.get(`/users/${id}`);
  }

  async createUser(userData: UserFormData): Promise<AxiosResponse<ApiResponse<User>>> {
    return this.api.post('/users', userData);
  }

  async updateUser(id: number, userData: Partial<UserFormData>): Promise<AxiosResponse<ApiResponse<User>>> {
    return this.api.put(`/users/${id}`, userData);
  }

  async deleteUser(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/users/${id}`);
  }

  async getUserStatistics(): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.get('/users/statistics');
  }

  async getUserActivityLog(userId: number, page = 1, limit = 20): Promise<AxiosResponse<ApiResponse<any>>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    return this.api.get(`/users/${userId}/activity-log?${params}`);
  }

  async bulkUsersAction(payload: any): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.post('/users/bulk-action', payload);
  }

  async importUsersCsv(file: File): Promise<AxiosResponse<ApiResponse<any>>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.api.post('/users/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000
    });
  }

  async exportUsers(format: 'csv' | 'excel', filters?: Record<string, any>): Promise<AxiosResponse<Blob>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.keys(filters).forEach((k) => {
        const v = (filters as any)[k];
        if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
      });
    }
    const qs = params.toString();
    return this.api.get(`/users/export/${format}${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
  }

  async resetUserPassword(userId: number, payload?: { mode?: 'direct' | 'token'; new_password?: string; send_email?: boolean }): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.post(`/users/${userId}/reset-password`, payload || {});
  }

  async unlockUserAccount(userId: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.post(`/users/${userId}/unlock-account`);
  }

  async getUserUnitsWithCategories(userId: number): Promise<AxiosResponse<ApiResponse<any[]>>> {
    return this.api.get(`/users/${userId}/units-with-categories`);
  }

  async toggleAccountLock(userId: number, isLocked: boolean, reason?: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.patch(`/users/${userId}/account-lock`, { is_locked: isLocked, reason });
  }

  // Roles endpoints
  async getRoles(): Promise<AxiosResponse<ApiResponse<Role[]>>> {
    return this.api.get('/roles');
  }

  async createRole(data: { name: string; description?: string; permissions?: string[] }): Promise<AxiosResponse<ApiResponse<Role>>> {
    return this.api.post('/admin/roles', data);
  }

  async updateRole(id: number, data: { name?: string; description?: string; permissions?: string[] }): Promise<AxiosResponse<ApiResponse<Role>>> {
    return this.api.put(`/admin/roles/${id}`, data);
  }

  async deleteRole(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/admin/roles/${id}`);
  }

  // Units endpoints
  async getUnitUsers(unitId: number): Promise<AxiosResponse<ApiResponse<Array<{ id: number; name: string; email: string; job_title: string | null }>>>> {
    return this.api.get(`/units/${unitId}/users`);
  }

  // Incidents endpoints
  async getIncidents(
    page = 1,
    limit = 10,
    filters?: {
      search?: string;
      incident_number?: string;
      title?: string;
      status?: string;
      priority?: string;
      unit_id?: number;
      department_id?: number;
      location?: string;
      area_id?: number;
      reported_by?: number;
      main_category_id?: number;
      sub_category_id?: number;
      closed_by?: number;
      date_from?: string;
      date_to?: string;
    }
  ): Promise<AxiosResponse<PaginatedResponse<Incident>>> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (filters) {
      Object.keys(filters).forEach(key => {
        const v = (filters as any)[key];
        if (v !== undefined && v !== null && v !== '') params.append(key, String(v));
      });
    }
    return this.api.get(`/incidents?${params}`);
  }

  async getIncidentsMeta(): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.get('/incidents/meta');
  }

  /** Incidents stats for summary cards. GET /api/incidents/stats */
  async getIncidentsStats(unitId?: number): Promise<AxiosResponse<ApiResponse<{ total: number; open: number; inProgress: number; resolved: number; closed: number; voided: number }>>> {
    const params = unitId ? { unit_id: unitId } : {};
    return this.api.get('/incidents/stats', { params });
  }

  async getIncidentAudit(incidentId: number): Promise<AxiosResponse<ApiResponse<any[]>>> {
    return this.api.get(`/incidents/${incidentId}/audit`);
  }

  async getIncidentById(id: number): Promise<AxiosResponse<ApiResponse<Incident>>> {
    return this.api.get(`/incidents/${id}`);
  }

  async createIncident(incidentData: IncidentFormData): Promise<AxiosResponse<ApiResponse<Incident>>> {
    const requestData: Record<string, unknown> = {
      title: incidentData.title,
      description: incidentData.description,
      category_id: incidentData.categoryId,
      unit_id: incidentData.unitId,
      priority: incidentData.priority,
      incident_date: incidentData.incidentDate,
      location: incidentData.location || undefined,
    };
    if (incidentData.departmentId != null && Number(incidentData.departmentId) > 0) {
      requestData.department_id = Number(incidentData.departmentId);
    }
    if (incidentData.affected_areas !== undefined) {
      requestData.affected_areas = incidentData.affected_areas;
    }
    if (incidentData.syncToAviSky !== undefined) {
      requestData.sync_to_avi_sky = incidentData.syncToAviSky;
    }
    return this.api.post('/incidents', requestData);
  }

  async updateIncident(
    id: number,
    incidentData: Partial<IncidentFormData> & { status?: string }
  ): Promise<AxiosResponse<ApiResponse<Incident>>> {
    // Transform camelCase to snake_case for backend
    // IMPORTANT: Only include fields that are explicitly provided to avoid overwriting existing data
    const requestData: any = {};
    
    // Only add fields that are explicitly set (not undefined)
    if (incidentData.title !== undefined) {
      requestData.title = incidentData.title;
    }
    if (incidentData.description !== undefined) {
      requestData.description = incidentData.description;
    }
    if (incidentData.categoryId !== undefined) {
      requestData.category_id = incidentData.categoryId;
    }
    if (incidentData.unitId !== undefined) {
      requestData.unit_id = incidentData.unitId;
    }
    if (incidentData.priority !== undefined) {
      requestData.priority = incidentData.priority;
    }
    if (incidentData.incidentDate !== undefined) {
      requestData.incident_date = incidentData.incidentDate;
    }
    if (incidentData.status !== undefined) {
      requestData.status = incidentData.status;
    }
    if (incidentData.location !== undefined) {
      requestData.location = incidentData.location;
    }
    if (incidentData.departmentId !== undefined) {
      requestData.department_id = incidentData.departmentId != null && Number(incidentData.departmentId) > 0 ? Number(incidentData.departmentId) : null;
    }
    if (incidentData.affected_areas !== undefined) {
      requestData.affected_areas = incidentData.affected_areas;
    }

    return this.api.put(`/incidents/${id}`, requestData);
  }

  async deleteIncident(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/incidents/${id}`);
  }

  async addIncidentComment(incidentId: number, comment: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.post(`/incidents/${incidentId}/comments`, { comment });
  }

  async sendIncidentEmail(incidentId: number, emails: string[]): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.post(`/incidents/${incidentId}/send-email`, { emails }, { timeout: 60000 });
  }

  // LHD Forms (MIDRMA F4)
  async getLHDStats(): Promise<AxiosResponse<ApiResponse<{ total: number; Draft: number; Submitted: number; 'Under Review': number; Approved: number; Rejected: number; Archived: number }>>> {
    return this.api.get('/lhd-forms/stats');
  }

  async getLHDForms(params?: {
    page?: number;
    limit?: number;
    status?: LHDStatus;
    unit_id?: number;
    date_from?: string;
    date_to?: string;
    severity?: string;
    search?: string;
  }): Promise<AxiosResponse<LHDFormListResponse>> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') searchParams.set(k, String(v));
      });
    }
    return this.api.get(`/lhd-forms?${searchParams.toString()}`);
  }

  async getLHDFormById(id: number): Promise<AxiosResponse<ApiResponse<LHDForm>>> {
    return this.api.get(`/lhd-forms/${id}`);
  }

  async sendLHDFormEmail(formId: number, emails: string[]): Promise<AxiosResponse<ApiResponse<{ messageId?: string }>>> {
    return this.api.post(`/lhd-forms/${formId}/send-email`, { emails }, { timeout: 60000 });
  }

  async getLHDFormHistory(id: number): Promise<AxiosResponse<ApiResponse<Array<{
    date: string;
    action: string;
    user_name: string;
    field_name?: string | null;
    old_value?: string | null;
    new_value?: string | null;
    ip_address?: string | null;
    user_agent?: string | null;
    comments?: string | null;
  }>>>> {
    return this.api.get(`/lhd-forms/${id}/history`);
  }

  async getLHDAnalytics(): Promise<AxiosResponse<ApiResponse<{
    top_aircraft_type: string;
    top_operator: string;
    max_flight_level: number | null;
    max_deviation: number | null;
    avg_duration_seconds: number | null;
    by_aircraft: Array<{ name: string; count: number }>;
    by_operator: Array<{ name: string; count: number }>;
  }>>> {
    return this.api.get('/lhd-forms/analytics');
  }

  async createLHDForm(data: Record<string, unknown>): Promise<AxiosResponse<ApiResponse<{ id: number; form_number: string; status: string }>>> {
    return this.api.post('/lhd-forms', data);
  }

  async updateLHDForm(id: number, data: Record<string, unknown>): Promise<AxiosResponse<ApiResponse<{ success: boolean; updated_at: string }>>> {
    return this.api.put(`/lhd-forms/${id}`, data);
  }

  async deleteLHDForm(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/lhd-forms/${id}`);
  }

  async submitLHDForm(id: number): Promise<AxiosResponse<ApiResponse<{ status: string }>>> {
    return this.api.post(`/lhd-forms/${id}/submit`, {});
  }

  async approveLHDForm(id: number, comments?: string): Promise<AxiosResponse<ApiResponse<{ status: string }>>> {
    return this.api.post(`/lhd-forms/${id}/approve`, { comments: comments ?? null });
  }

  async rejectLHDForm(id: number, comments?: string): Promise<AxiosResponse<ApiResponse<{ status: string }>>> {
    return this.api.post(`/lhd-forms/${id}/reject`, { comments: comments ?? null });
  }

  async copyLHDForm(id: number): Promise<AxiosResponse<ApiResponse<{ id: number; form_number: string; status: string }>>> {
    return this.api.post(`/lhd-forms/${id}/copy`, {});
  }

  async getLHDFormAutofill(incidentId: number): Promise<AxiosResponse<ApiResponse<Record<string, unknown>>>> {
    return this.api.get(`/lhd-forms/autofill/${incidentId}`);
  }

  async getLHDCauses(): Promise<AxiosResponse<ApiResponse<string[]>>> {
    return this.api.get('/lhd-forms/meta/causes');
  }

  // Aviation Library (Master Data for LHD)
  async getAviationOperators(search?: string): Promise<AxiosResponse<ApiResponse<Array<{ id: number; name: string; code?: string; country?: string }>>>> {
    return this.api.get('/aviation-library/operators', { params: search ? { search } : {} });
  }
  async addAviationOperator(data: { name: string; code?: string; country?: string }): Promise<AxiosResponse<ApiResponse<{ id: number }>>> {
    return this.api.post('/aviation-library/operators', data);
  }
  async deleteAviationOperator(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/aviation-library/operators/${id}`);
  }
  async getAviationAircraftTypes(search?: string): Promise<AxiosResponse<ApiResponse<Array<{ id: number; name: string; manufacturer?: string }>>>> {
    return this.api.get('/aviation-library/aircraft-types', { params: search ? { search } : {} });
  }
  async addAviationAircraftType(data: { name: string; manufacturer?: string }): Promise<AxiosResponse<ApiResponse<{ id: number }>>> {
    return this.api.post('/aviation-library/aircraft-types', data);
  }
  async deleteAviationAircraftType(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/aviation-library/aircraft-types/${id}`);
  }
  async getAviationCallSigns(search?: string): Promise<AxiosResponse<ApiResponse<string[]>>> {
    return this.api.get('/aviation-library/call-signs', { params: search ? { search } : {} });
  }
  async getAviationRegistrations(search?: string): Promise<AxiosResponse<ApiResponse<Array<{ id: number; registration: string; operator_id?: number; aircraft_type_id?: number; operator_name?: string; aircraft_type_name?: string }>>>> {
    return this.api.get('/aviation-library/registrations', { params: search ? { search } : {} });
  }
  async addAviationRegistration(data: { registration: string; operator_id?: number; aircraft_type_id?: number }): Promise<AxiosResponse<ApiResponse<{ id: number }>>> {
    return this.api.post('/aviation-library/registrations', data);
  }
  async deleteAviationRegistration(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/aviation-library/registrations/${id}`);
  }
  async getAviationDeviationCauses(): Promise<AxiosResponse<ApiResponse<Array<{ id: number; name: string; sort_order: number }>>>> {
    return this.api.get('/aviation-library/deviation-causes');
  }
  async addAviationDeviationCause(data: { name: string }): Promise<AxiosResponse<ApiResponse<{ id: number }>>> {
    return this.api.post('/aviation-library/deviation-causes', data);
  }
  async deleteAviationDeviationCause(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/aviation-library/deviation-causes/${id}`);
  }

  async exportLHDFormPDF(id: number, maskPersonal = false): Promise<AxiosResponse<Blob>> {
    return this.api.get(`/lhd-forms/${id}/export${maskPersonal ? '?mask=true' : ''}`, { responseType: 'blob' });
  }

  // Categories endpoints (no-cache to avoid stale status/buttons)
  async getCategories(): Promise<AxiosResponse<ApiResponse<Category[]>>> {
    return this.api.get('/categories', {
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      params: { _: Date.now() }
    });
  }

  /** Primary categories for current user's unit (Incidents category circles). GET /api/categories/for-user */
  async getCategoriesForUser(): Promise<AxiosResponse<ApiResponse<Array<{ id: number; name: string; name_ar?: string; color?: string; code?: string }>>>> {
    return this.api.get('/categories/for-user');
  }

  async getCategoryStatistics(): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.get('/categories/stats');
  }

  async getCategoryDetails(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.get(`/categories/${id}`);
  }

  async getCategoryActivity(id: number): Promise<AxiosResponse<ApiResponse<any[]>>> {
    return this.api.get(`/categories/${id}/activity`);
  }

  // Profile Picture endpoints
  async uploadProfilePicture(userId: number, file: File): Promise<AxiosResponse<ApiResponse<{ file_url: string }>>> {
    const formData = new FormData();
    formData.append('profile_picture', file);
    return this.api.post(`/users/${userId}/profile-picture`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async deleteProfilePicture(userId: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/users/${userId}/profile-picture`);
  }

  // Incident Attachments endpoints
  async uploadIncidentAttachments(incidentId: number, files: File[]): Promise<AxiosResponse<ApiResponse<{ attachments: any[] }>>> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('attachments', file);
    });
    return this.api.post(`/incidents/${incidentId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async deleteIncidentAttachment(incidentId: number, attachmentId: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/incidents/${incidentId}/attachments/${attachmentId}`);
  }

  // Settings endpoints
  async getUnitCategoryPermissions(unitId?: number, categoryId?: number): Promise<AxiosResponse<ApiResponse<any[]>>> {
    const params = new URLSearchParams();
    if (unitId) params.append('unit_id', unitId.toString());
    if (categoryId) params.append('category_id', categoryId.toString());
    return this.api.get(`/settings/unit-category-permissions?${params}`);
  }

  async createUnitCategoryPermission(data: { unit_id: number; category_id: number; can_view: boolean; can_create: boolean; can_update: boolean; can_delete: boolean }): Promise<AxiosResponse<ApiResponse<{ id: number }>>> {
    return this.api.post('/settings/unit-category-permissions', data);
  }

  async updateUnitCategoryPermission(id: number, data: { can_view: boolean; can_create: boolean; can_update: boolean; can_delete: boolean }): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.put(`/settings/unit-category-permissions/${id}`, data);
  }

  async deleteUnitCategoryPermission(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/settings/unit-category-permissions/${id}`);
  }

  async getAccessibleCategories(unitId: number): Promise<AxiosResponse<ApiResponse<Category[]>>> {
    return this.api.get(`/settings/units/${unitId}/accessible-categories`);
  }

  // PDF Export endpoint
  async downloadIncidentPDF(incidentId: number): Promise<Blob> {
    const response = await this.api.get(`/reports/incidents/${incidentId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async createCategory(categoryData: Omit<Category, 'id'>): Promise<AxiosResponse<ApiResponse<Category>>> {
    return this.api.post('/categories', categoryData);
  }

  async updateCategory(id: number, categoryData: Partial<Category>): Promise<AxiosResponse<ApiResponse<Category>>> {
    return this.api.put(`/categories/${id}`, categoryData);
  }

  async deleteCategory(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/categories/${id}`);
  }

  async exportCategoriesExcel(): Promise<AxiosResponse<Blob>> {
    return this.api.get('/categories/export/excel', {
      responseType: 'blob'
    });
  }

  // Units endpoints
  async getUnits(): Promise<AxiosResponse<ApiResponse<Unit[]>>> {
    return this.api.get('/units');
  }

  async getUnitStatistics(): Promise<AxiosResponse<ApiResponse<{
    total: number;
    active: number;
    inactive: number;
    withoutCategories: number;
    withUsers: number;
    highestLoad: { unitName: string; openIncidentsCount: number } | null;
  }>>> {
    return this.api.get('/units/statistics');
  }

  async getUnitDetails(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.get(`/units/${id}/details`);
  }

  async getUnitLoadAnalysis(unitId?: number): Promise<AxiosResponse<ApiResponse<Array<{
    unitName: string;
    openIncidentsCount: number;
  }>>>> {
    const url = unitId ? `/units/${unitId}/load-analysis` : '/units/load-analysis';
    return this.api.get(url);
  }

  async getUnitManagedCategories(unitId: number): Promise<AxiosResponse<ApiResponse<any[]>>> {
    return this.api.get(`/units/${unitId}/categories`);
  }

  async createUnit(unitData: Omit<Unit, 'id'>): Promise<AxiosResponse<ApiResponse<Unit>>> {
    return this.api.post('/units', unitData);
  }

  async updateUnit(id: number, unitData: Partial<Unit>): Promise<AxiosResponse<ApiResponse<Unit>>> {
    return this.api.put(`/units/${id}`, unitData);
  }

  async deleteUnit(id: number): Promise<AxiosResponse<ApiResponse<{ canDelete: boolean; reason?: string }>>> {
    return this.api.delete(`/units/${id}`);
  }

  async exportUnitsExcel(): Promise<AxiosResponse<Blob>> {
    return this.api.get('/units/export/excel', {
      responseType: 'blob'
    });
  }

  async getUnitDepartments(unitId: number): Promise<AxiosResponse<ApiResponse<Array<{ id: number; name: string }>>>> {
    return this.api.get(`/units/${unitId}/departments`);
  }

  async assignUnitDepartments(unitId: number, departmentIds: number[]): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.post(`/units/${unitId}/departments`, { departmentIds });
  }

  async getUnitAssignedUsers(unitId: number): Promise<AxiosResponse<ApiResponse<User[]>>> {
    return this.api.get(`/units/${unitId}/assigned-users`);
  }

  async assignUnitUsers(unitId: number, userIds: number[]): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.post(`/units/${unitId}/assign-users`, { userIds });
  }

  /** Single call for dashboard filter data (units, categories, areas). Use instead of 3 separate calls. */
  /** Public landing page stats (no auth). GET /api/public/summary-stats */
  async getPublicSummaryStats(): Promise<AxiosResponse<ApiResponse<{
    totalIncidents: number;
    openIncidents: number;
    totalLhdForms: number;
    totalHandovers: number;
    totalUsers: number;
  }>>> {
    return this.api.get('/public/summary-stats');
  }

  async getDashboardInit(): Promise<AxiosResponse<ApiResponse<{
    units: Unit[];
    categories: Category[];
    areas: Array<{ id: number; name: string; name_ar?: string; location_id?: number; unit_id?: number }>;
  }>>> {
    return this.api.get('/dashboard/init');
  }

  // Reports endpoints
  async getDashboardStats(): Promise<AxiosResponse<ApiResponse<DashboardStats>>> {
    return this.api.get('/reports/dashboard');
  }

  /** Recent activity for dashboard (from audit_logs). GET /api/reports/dashboard/activity */
  async getDashboardActivity(limit = 10): Promise<AxiosResponse<ApiResponse<Array<{
    id: number;
    type: 'new' | 'closed' | 'updated' | 'critical';
    message: string;
    timestamp: string;
    user: string;
    incidentNumber?: string;
  }>>>> {
    return this.api.get(`/reports/dashboard/activity?limit=${limit}&_=${Date.now()}`);
  }

  async getDashboardAnalytics(filters?: {
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    priority?: string;
    categoryId?: number;
    unitId?: number;
    q?: string;
    my?: boolean;
  }): Promise<AxiosResponse<ApiResponse<any>>> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.categoryId) params.append('categoryId', String(filters.categoryId));
      if (filters.unitId) params.append('unitId', String(filters.unitId));
      if (filters.q) params.append('q', filters.q);
      if (filters.my) params.append('my', '1');
    }
    if (filters) params.append('_', String(Date.now()));
    const qs = params.toString();
    return this.api.get(`/reports/dashboard/analytics?${qs || '_=' + Date.now()}`);
  }

  async getIncidentsReport(filters?: {
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    priority?: string;
    severity?: string;
    severity_level?: string;
    categoryId?: number;
    category_id?: number;
    unitId?: number;
    unit_id?: number;
    areaId?: number;
    q?: string;
    my?: boolean | string | number;
    page?: number;
    limit?: number;
    sort?: string;
    order?: string;
  }): Promise<AxiosResponse<any>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key as keyof typeof filters]) {
          params.append(key, String(filters[key as keyof typeof filters]));
        }
      });
    }
    return this.api.get(`/reports/incidents?${params}`);
  }

  async exportIncidentsCSV(filters?: any): Promise<AxiosResponse<Blob>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });
    }
    return this.api.get(`/reports/incidents/export?${params}`, {
      responseType: 'blob'
    });
  }

  // Monitor (Admin Command Center) - Admin & Super Admin only
  async getMonitorData(params?: { from?: string; to?: string }): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.get('/admin/monitor', { params });
  }
  async getMonitorHealth(): Promise<AxiosResponse<ApiResponse<{ ok: boolean; db: string }>>> {
    return this.api.get('/admin/monitor/health');
  }

  // AVISky outbox admin monitoring
  async getAviSkyOutboxSummary(): Promise<AxiosResponse<ApiResponse<import('../types').AviSkyOutboxSummary>>> {
    return this.api.get('/admin/avisky-outbox/summary');
  }

  async getAviSkyOutboxEvents(params?: {
    status?: string;
    incidentId?: number;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<AxiosResponse<ApiResponse<import('../types').AviSkyOutboxEvent[]>>> {
    return this.api.get('/admin/avisky-outbox', { params });
  }

  async getIncidentAviSkyIntegration(incidentId: number): Promise<AxiosResponse<ApiResponse<import('../types').IncidentAviSkyIntegration>>> {
    return this.api.get(`/admin/avisky-outbox/incidents/${incidentId}`);
  }

  async retryAviSkyOutboxEvent(outboxId: number): Promise<AxiosResponse<ApiResponse<import('../types').AviSkyOutboxEvent>>> {
    return this.api.post(`/admin/avisky-outbox/${outboxId}/retry`, {});
  }

  // AI (Gemini) - suggest fields from narrative; analyze trends for monitor
  async aiSuggestFields(payload: { narrative: string; language?: 'ar' | 'en' }): Promise<AxiosResponse<ApiResponse<{
    severity: 'Low' | 'Medium' | 'High' | 'Critical';
    rootCause: string;
    summary: string;
    suggestedCause?: string;
  }>>> {
    return this.api.post('/ai/suggest-fields', payload, { timeout: 25000 });
  }

  async aiSuggestLhdForm(payload: Record<string, unknown>): Promise<AxiosResponse<ApiResponse<{
    detailed_description: string;
    severity: 'Low' | 'Medium' | 'High' | 'Critical';
    suggestedCause: string;
    crew_comments?: string;
    rootCause: string;
    summary: string;
  }>>> {
    return this.api.post('/ai/suggest-lhd-form', { form: payload }, { timeout: 30000 });
  }

  async aiSuggestIncidentForm(payload: Record<string, unknown>): Promise<AxiosResponse<ApiResponse<{
    description: string;
    priority: 'Low' | 'Medium' | 'High' | 'Critical';
    title: string;
    rootCause: string;
    summary: string;
  }>>> {
    return this.api.post('/ai/suggest-incident-form', { form: payload }, { timeout: 30000 });
  }

  async aiAnalyzeTrends(params?: { limit?: number }): Promise<AxiosResponse<ApiResponse<{
    overallStatus: string;
    blindSpots: string[];
    frequentHumanFactors: string[];
    preventiveActions: string[];
    reportLanguage: 'ar' | 'en';
  }>>> {
    return this.api.post('/ai/analyze-trends', params ?? {}, { timeout: 35000 });
  }

  // Database Management (Backup, Verify) - Super Admin only.
  private static readonly DATABASE_TIMEOUT_MS = 30000;
  private static readonly BACKUP_DOWNLOAD_TIMEOUT_MS = 10 * 60 * 1000;

  async getDatabaseStats(): Promise<AxiosResponse<ApiResponse<{
    engine: string;
    sizeBytes: number;
    sizeFormatted: string;
    totalRecords: number;
    tablesCount: number;
  }>>> {
    return this.api.get('/admin/database/stats', { timeout: ApiService.DATABASE_TIMEOUT_MS });
  }

  /** Triggers full backup and returns blob; caller should create download link from response.data. */
  async downloadBackup(): Promise<AxiosResponse<Blob>> {
    return this.api.get('/admin/database/backup/download', { responseType: 'blob', timeout: ApiService.BACKUP_DOWNLOAD_TIMEOUT_MS });
  }

  async getBackups(): Promise<AxiosResponse<ApiResponse<Array<{
    filename: string;
    sizeBytes: number;
    sizeFormatted: string;
    createdAt: string;
  }>>>> {
    return this.api.get('/admin/database/backups', { timeout: ApiService.DATABASE_TIMEOUT_MS });
  }

  /** Download a specific backup file by filename. Returns blob. */
  async downloadBackupFile(filename: string): Promise<AxiosResponse<Blob>> {
    return this.api.get(`/admin/database/backups/${encodeURIComponent(filename)}/download`, { responseType: 'blob', timeout: ApiService.BACKUP_DOWNLOAD_TIMEOUT_MS });
  }

  async deleteBackup(filename: string): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/admin/database/backups/${encodeURIComponent(filename)}`, { timeout: ApiService.DATABASE_TIMEOUT_MS });
  }

  async verifyIntegrity(repair: boolean = true): Promise<AxiosResponse<ApiResponse<{
    checked: number;
    errors: Array<{ table: string; message: string }>;
    repaired: string[];
  }>>> {
    return this.api.post('/admin/database/verify-integrity', { repair }, { timeout: ApiService.DATABASE_TIMEOUT_MS });
  }

  // Configuration endpoints
  async getConfiguration(): Promise<AxiosResponse<ApiResponse<{
    departments: string[];
    job_titles: string[];
    raw_departments: any[];
    raw_job_titles: any[];
  }>>> {
    return this.api.get('/configuration');
  }

  async addDepartment(name: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.post('/configuration/departments', { name });
  }

  async deleteDepartment(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/configuration/departments/${id}`);
  }

  async addJobTitle(name: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.post('/configuration/job-titles', { name });
  }

  async deleteJobTitle(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/configuration/job-titles/${id}`);
  }

  // Locations (unit-specific)
  async getLocations(unitId?: number): Promise<AxiosResponse<ApiResponse<Array<{ id: number; name: string; unit_id: number; unit_name?: string }>>>> {
    const params = unitId != null ? { unit_id: unitId } : {};
    return this.api.get('/configuration/locations', { params });
  }

  async addLocation(name: string, unitId: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.post('/configuration/locations', { name, unit_id: unitId });
  }

  async updateLocation(id: number, data: { name?: string; unit_id?: number }): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.put(`/configuration/locations/${id}`, data);
  }

  async deleteLocation(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/configuration/locations/${id}`);
  }

  // Areas (Sectors) – unit + location
  async getAreas(unitId?: number, locationId?: number): Promise<AxiosResponse<ApiResponse<Array<{ id: number; name: string; name_ar?: string; location_id: number; unit_id: number; location_name?: string; unit_name?: string }>>>> {
    const params: Record<string, number> = {};
    if (unitId != null) params.unit_id = unitId;
    if (locationId != null) params.location_id = locationId;
    return this.api.get('/configuration/areas', { params });
  }

  async addArea(data: { name: string; name_ar?: string; location_id: number; unit_id: number }): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.post('/configuration/areas', data);
  }

  async updateArea(id: number, data: { name?: string; name_ar?: string; location_id?: number; unit_id?: number }): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.put(`/configuration/areas/${id}`, data);
  }

  async deleteArea(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/configuration/areas/${id}`);
  }

  // Shift Handover endpoints
  async getShiftHandoverAreas(): Promise<AxiosResponse<ApiResponse<Array<{ id: number; name: string; name_ar?: string; location_id: number; unit_id: number }>>>> {
    return this.api.get('/shift-handovers/areas');
  }

  async getShiftHandoverStats(): Promise<AxiosResponse<ApiResponse<{ totalHandovers: number; todayHandovers: number; ongoingIncidents: number }>>> {
    return this.api.get('/shift-handovers/stats');
  }

  async getShiftHandoverList(limit = 5): Promise<AxiosResponse<ApiResponse<Array<{
    id: number;
    shift?: string;
    next_shift?: string | null;
    team?: string;
    outgoing_controller?: string;
    incoming_controller?: string;
    handover_at_utc?: string;
    created_at?: string;
    operational_notes?: string | null;
    notams?: string | null;
    equipment_status?: string | null;
    area_statuses?: string | Record<number, string> | null;
    incident_ids?: string | number[] | null;
  }>>>> {
    return this.api.get('/shift-handovers', { params: { limit } });
  }

  async getShiftHandoverIncidents(): Promise<AxiosResponse<ApiResponse<{
    incidents: any[];
    openIncidents: any[];
    fromUtc: string;
    toUtc: string;
  }>>> {
    return this.api.get('/shift-handovers/incidents');
  }

  async getShiftHandoverById(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.get(`/shift-handovers/${id}`);
  }

  async sendShiftHandoverEmail(handoverId: number, emails: string[]): Promise<AxiosResponse<ApiResponse<{ messageId?: string }>>> {
    return this.api.post(`/shift-handovers/${handoverId}/send-email`, { emails }, { timeout: 60000 });
  }

  async updateShiftHandover(id: number, data: {
    operational_notes?: string;
    notams?: string;
    equipment_status?: string;
    area_statuses?: Record<number, string>;
    incident_ids?: number[];
  }): Promise<AxiosResponse<ApiResponse<{ id: number }>>> {
    return this.api.put(`/shift-handovers/${id}`, data);
  }

  async deleteShiftHandover(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.api.delete(`/shift-handovers/${id}`);
  }

  async createShiftHandover(data: {
    team?: string;
    shift?: string;
    next_shift?: string;
    outgoing_controller?: string;
    incoming_controller?: string;
    current_controller_id?: number;
    next_shift_user_id?: number;
    runway_26l_08r_active?: boolean;
    ils_out_of_service?: boolean;
    lvp_active?: boolean;
    bird_activity_reported?: boolean;
    operational_notes?: string;
    notams?: string;
    equipment_status?: string;
    incident_ids?: number[];
    area_statuses?: Record<number, 'Active' | 'Closed'>;
    incident_handover_notes?: Record<number, string>;
  }): Promise<AxiosResponse<ApiResponse<{ id: number }>>> {
    return this.api.post('/shift-handovers', data);
  }
}

export const apiService = new ApiService();
export default apiService;
